import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, IconButton } from '@mui/material';

import CapturePhoto from '../CapturePhoto';
import ControlledError from '../ControlledError';
import Image from '../Image';
import { ReactComponent as PlusIcon } from 'assets/icons/Plus.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/ClearRed.svg';
import { ReactComponent as DocumentIcon } from 'assets/icons/DocumentIcon.svg';

import { IFile } from 'models/general';
import { useStyles } from './styles';
import { getImageFormat } from 'utils/files';

const PDF_MIME_TYPE = 'application/pdf';

export interface IAddImagesProps {
  images: Array<IFile>;
  isUploading?: boolean;
  deleteId?: string;
  required?: boolean;
  skippable?: boolean;
  error?: string;
  containerStyleClass?: string;
  textContainerStyleClass?: string;
  maxImages?: number;
  isLoadingImages?: boolean;
  disabled?: boolean;
  /** Base id for automation. The add trigger uses `${testId}-add-button`, each preview
   * `${testId}-preview-{index}` and its delete button `${testId}-delete-button-{index}` (0-based). */
  testId?: string;
  onCapture: (image: string | string[]) => void;
  onDelete: (file: IFile) => void;
  multiple?: boolean;
  /** Allows picking PDF files as well as images (on by default) */
  isPdfEnabled?: boolean;
}

export interface IImagePreviewProps {
  id?: string;
  image: string;
  isLoading?: boolean;
  disabled?: boolean;
  deleteId?: string;
  hasFileId?: boolean;
  testId?: string;
  index?: number;
  onDelete?: () => void;
}

export interface IPdfPreviewProps {
  image: string;
  deleteId?: string;
  isLoading?: boolean;
  disabled?: boolean;
  testId?: string;
  index?: number;
  onDelete?: () => void;
  id?: string;
  hasFileId?: boolean;
}

const ImagePreview = ({
  id,
  image,
  isLoading,
  deleteId,
  hasFileId,
  disabled,
  testId,
  index,
  onDelete,
}: IImagePreviewProps) => {
  const classes = useStyles();
  return (
    <div
      className={`${classes.imagePreview} ${(isLoading && !hasFileId) || id === deleteId ? classes.imagePreviewLoading : ''}`}
      data-testid={testId ? `${testId}-preview-${index}` : undefined}
    >
      <Image src={image} alt="Preview" className={classes.image} fallbackClassName={classes.fallbackImage} />
      {!deleteId && !isLoading && !disabled && (
        <IconButton
          aria-label="close"
          data-testid={testId ? `${testId}-delete-button-${index}` : undefined}
          className={classes.deleteIconParent}
          onClick={id !== deleteId && onDelete}
        >
          <DeleteIcon />
        </IconButton>
      )}
      {((isLoading && !hasFileId) || id === deleteId) && <CircularProgress className={classes.spinner} size={24} />}
    </div>
  );
};

const PdfPreview = ({
  image,
  deleteId,
  isLoading,
  disabled,
  testId,
  index,
  onDelete,
  id,
  hasFileId,
}: IPdfPreviewProps) => {
  const classes = useStyles();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Certification presigned URLs often carry a signed `response-content-disposition=attachment`,
  // which Chrome honours inside an <iframe> and turns into an immediate "Save As". The disposition
  // is part of the CloudFront signature so it cannot be stripped client-side, but fetch() ignores
  // both Content-Disposition and Content-Type, so we re-wrap the bytes in a blob we tag as
  // application/pdf ourselves — the resulting blob: URL always renders inline. Only if the fetch
  // itself fails (CORS, network, non-2xx) do we fall back to a clickable document icon that opens
  // the file in a new tab.
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    setBlobUrl(null);
    setLoadError(false);

    const load = async () => {
      try {
        let blob: Blob;
        if (image.startsWith('data:')) {
          const base64Data = image.split(',')[1];
          const binary = atob(base64Data);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          blob = new Blob([bytes], { type: PDF_MIME_TYPE });
        } else {
          const response = await fetch(image);
          if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
          blob = new Blob([await response.arrayBuffer()], { type: PDF_MIME_TYPE });
        }
        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
        }
      } catch {
        if (!cancelled) setLoadError(true);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [image]);

  return (
    <div
      className={`${classes.imagePreview} ${(isLoading && !hasFileId) || id === deleteId ? classes.imagePreviewLoading : ''}`}
      data-testid={testId ? `${testId}-preview-${index}` : undefined}
    >
      <div className={classes.pdfPreview}>
        {blobUrl ? (
          <iframe src={blobUrl} title="PDF Preview" className={classes.pdfIframe} />
        ) : (
          <div
            className={`${classes.pdfPlaceholder} ${loadError ? classes.pdfClickable : ''}`}
            data-testid={testId ? `${testId}-open-button-${index}` : undefined}
            onClick={loadError ? () => window.open(image, '_blank', 'noopener,noreferrer') : undefined}
          >
            {loadError ? (
              <>
                <DocumentIcon className={classes.pdfFallbackIcon} />
                <span className={classes.pdfLabel}>PDF</span>
              </>
            ) : (
              <CircularProgress size={24} />
            )}
          </div>
        )}
      </div>
      {!deleteId && !isLoading && !disabled && (
        <IconButton
          aria-label="close"
          data-testid={testId ? `${testId}-delete-button-${index}` : undefined}
          className={classes.deleteIconParent}
          onClick={id !== deleteId && onDelete}
        >
          <DeleteIcon />
        </IconButton>
      )}
      {((isLoading && !hasFileId) || id === deleteId) && <CircularProgress className={classes.spinner} size={24} />}
    </div>
  );
};

const AddImages = ({
  images,
  isUploading,
  deleteId,
  required,
  skippable,
  disabled,
  error,
  containerStyleClass = '',
  textContainerStyleClass = '',
  maxImages,
  isLoadingImages,
  testId,
  onCapture,
  onDelete,
  multiple = false,
  isPdfEnabled = true,
}: IAddImagesProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [showCaptureModal, setShowCaptureModal] = useState(false);

  const onProceed = (imageSrc: string | string[]) => {
    onCapture(imageSrc);
    setShowCaptureModal(false);
  };

  const isMaxImagesReached = maxImages && images.length >= maxImages;

  return (
    <div className={`${containerStyleClass}`}>
      {images?.length > 0 && (
        <div className={classes.imagePreviewContainer}>
          {images.map((file, index) => {
            let imageFormat = '';
            if (file.url) {
              imageFormat = file.displayName ? file.displayName.split('.').pop() : '';
            } else {
              imageFormat = getImageFormat(file.image);
            }
            if (imageFormat === 'pdf') {
              return (
                <PdfPreview
                  key={file.id || file.tempId}
                  image={file.url || file.image}
                  deleteId={deleteId}
                  isLoading={isUploading}
                  disabled={disabled}
                  testId={testId}
                  index={index}
                  onDelete={() => onDelete(file)}
                  hasFileId={!!file.id}
                  id={file.id || file.tempId}
                />
              );
            }
            return (
              <ImagePreview
                key={file.id || file.tempId}
                id={file.id || file.tempId}
                image={file.url || file.image}
                isLoading={isUploading}
                deleteId={deleteId}
                hasFileId={!!file.id}
                disabled={disabled}
                testId={testId}
                index={index}
                onDelete={() => onDelete(file)}
              />
            );
          })}
        </div>
      )}
      {!isMaxImagesReached && (
        <ControlledError show={!!error} error={error}>
          <div
            className={`${classes.addImageContainer} ${isLoadingImages || disabled ? classes.cursorNotAllowed : ''} ${textContainerStyleClass}`}
            data-testid={testId ? `${testId}-add-button` : undefined}
            onClick={() => !isLoadingImages && !disabled && setShowCaptureModal(true)}
          >
            {isLoadingImages ? <CircularProgress size={24} /> : <PlusIcon />}
            <p className={classes.addImageText}>
              {isLoadingImages ? t('common.loadingImages') : t('common.addImage')}
              {(required || skippable) && !isLoadingImages && (
                <span className={required ? classes.requiredMark : classes.skippableMark}>{required ? '*' : '**'}</span>
              )}
            </p>
          </div>
        </ControlledError>
      )}
      {showCaptureModal && (
        <CapturePhoto
          onClose={() => setShowCaptureModal(false)}
          onProceed={onProceed}
          multiple={multiple}
          isPdfEnabled={isPdfEnabled}
        />
      )}
    </div>
  );
};

export default memo(AddImages);
